import React, { useEffect, useState } from "react";
import { useParams, useLocation, useNavigate } from "react-router-dom";
import { default as SiteForm } from "./Components/SiteForm";
import { getSiteById, postSites, updateSiteById } from "../../Services/siteService";
import { getAllUsers } from "../../Services/UserService";
import { getAllTagOptions } from "../../Services/zohoTagService";
import toast from "react-hot-toast";
import * as routes from "../../routes";
import * as constants from "./constants";
import { GET_ALL_PAGINATION } from "../../Constants/Strings";
import { extractErrorMessage } from "../../Utils/extractErrorMessage";

const SiteOperations = () => {
  const { siteId } = useParams();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  const [siteData, setSiteData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [siteInchargeList, setSiteInchargeList] = useState([]);
  const [projectInchargeList, setProjectInchargeList] = useState([]);
  const [tagOptionsList, setTagOptionsList] = useState([]);
  const [allUsersList, setAllUsersList] = useState([]);

  const mode = pathname.includes("/edit")
    ? constants.EDIT_MODE
    : pathname.includes("/view")
      ? constants.VIEW_MODE
      : constants.ADD_MODE;

  const defaultInitialValues = {
    name: "",
    client: {
      name: "",
      email: "",
      phone: "",
      address: "",
    },
    estimated_start_date: "",
    estimated_end_date: "",
    geofence: null,
    site_incharge: "",
    project_incharge: "",
    is_active: true,
    tag_option: "",
  };

  // Fetch incharge lists
  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const [siteUsersRes, projectUsersRes] = await Promise.all([
          getAllUsers({ role: constants.ROLES.INCHARGE }),
          getAllUsers({ role: constants.ROLES.PROJECT_INCHARGE }),
        ]);
        setSiteInchargeList(siteUsersRes.data.results || []);
        setProjectInchargeList(projectUsersRes.data.results || []);
      } catch (err) {
        toast.error(constants.LOAD_INCHARGE_FAILED);
      }
    };

    fetchUsers();
  }, []);

  // Fetch tag options (HR-only dropdown)
  useEffect(() => {
    const fetchTagOptions = async () => {
      try {
        const options = await getAllTagOptions(GET_ALL_PAGINATION);
        setTagOptionsList(options);
      } catch (err) {
        toast.error(constants.LOAD_TAG_OPTIONS_FAILED);
      }
    };

    fetchTagOptions();
  }, []);

  // Fetch all users (HR-only user-to-site mapping)
  useEffect(() => {
    const fetchAllUsers = async () => {
      try {
        const response = await getAllUsers(GET_ALL_PAGINATION);
        setAllUsersList(response.data.results || []);
      } catch (err) {
        toast.error(constants.LOAD_USERS_FAILED);
      }
    };

    fetchAllUsers();
  }, []);

  // Fetch site data if edit/view
  useEffect(() => {
    const fetchData = async () => {
      if (mode === constants.ADD_MODE) {
        setSiteData(defaultInitialValues);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await getSiteById(siteId);
        if (response.status === 200) {
          setSiteData(response.data);
        } else {
          const msg = extractErrorMessage(response) || constants.SITE_NOT_FOUND;
          toast.error(msg);
        }
      } catch (err) {
        const msg = extractErrorMessage(err) || constants.SITE_LOAD_FAILED;
        toast.error(msg);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [mode, siteId]);

  const handleSubmit = async (payload) => {
    try {
      const response =
        mode === constants.ADD_MODE
          ? await postSites(payload)
          : await updateSiteById(siteId, payload);

      if (response.status === 200 || response.status === 201) {
        toast.success(
          mode === constants.ADD_MODE
            ? constants.SITE_CREATE_SUCCESS
            : constants.SITE_UPDATE_SUCCESS
        );
        navigate(routes.SITE);
      } else {
        const msg = extractErrorMessage(response) || constants.SITE_SAVE_FAILED;
        toast.error(msg);
      }
    } catch (error) {
      const msg = extractErrorMessage(error) || constants.SITE_SAVE_FAILED;
      toast.error(msg);
    }
  };


  return (
    <div className="relative z-0 max-w-3xl mx-auto px-4 mt-4 mb-6 overflow-visible">
      <SiteForm
        mode={mode}
        initialValues={siteData}
        onSubmit={handleSubmit}
        loading={loading}
        submitText={mode === constants.EDIT_MODE ? "Update Site" : "Create Site"}
        siteInchargeList={siteInchargeList}
        projectInchargeList={projectInchargeList}
        tagOptionsList={tagOptionsList}
        allUsersList={allUsersList}
      />
    </div>
  );
};

export default SiteOperations;
